import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, getTeams } from "../lib/api";
import { formatDate } from "../lib/date";
import { NavPageTitle } from "./NavPageTitle";
import { TeamSortToggle } from "./TeamSortToggle";
import { FilterTrigger, DrawerTitle } from "./TabNavToggles";
import { PwhlTeamButtons } from "./PwhlTeamButtons";
import { SegmentedControl } from "./SegmentedControl";
import { TeamBadge } from "./TeamBadge";
import { PositionFilterTabs } from "./PositionFilterTabs";
import { useSortByTeam, TEAM_GROUP } from "../lib/SortByTeamContext";
import { orderTeams } from "../lib/teamMap";

const STAGE_ORDER = { preseason: 0, regular: 1, playoffs: 2 };
const STAGE_LABEL = { preseason: "PRE", regular: "RS", playoffs: "PO" };

// Shared picker for the team Rosters / Lineups tabs — standardised drawer nav:
// Title + Active + Season / Stage FilterTriggers + Team Pick, one shared drawer
// row below, then the team-vs-team game bar. `target` is "active" or a game id.
export function RosterGamePicker({ teamId, target, onTarget, titlePage, count, extra = null, includeActive = true, activeLabel = "Active Roster", historicLabel = null, availType = "roster", position = null, onPosition = null, posOrder = null }) {
    const { openGroup, setOpenGroup, setDefaultGroup, setOwnTeamDrawer, sortTeam, onTeamSelect, setSortTeam, allowAll, favTeam, teamIds } = useSortByTeam();
    const { data: teamRows = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const teamSortIds = useMemo(() => {
        const loaded = new Set(orderTeams(teamRows).map((t) => t.id));
        return (teamIds && teamIds.length ? teamIds : orderTeams(teamRows).map((t) => t.id)).filter((id) => loaded.has(id));
    }, [teamRows, teamIds]);
    useEffect(() => {
        setDefaultGroup("season"); setOpenGroup("season"); setOwnTeamDrawer(true);
        return () => { setOwnTeamDrawer(false); setDefaultGroup(null); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const handleTeamSelect = (v, e) => { (onTeamSelect ? onTeamSelect(v || null) : setSortTeam(v || null)); if (!e?.shiftKey) setOpenGroup("season"); };

    const { data: fixtures = [] } = useQuery({
        queryKey: ["team-fixtures-picker", teamId],
        queryFn: () => api.get(`/pwhl/schedule?team_id=${teamId}`).then((r) => r.data),
    });
    const { data: availList = [] } = useQuery({
        queryKey: ["pwhl-game-availability"],
        queryFn: () => api.get("/pwhl/game-availability").then((r) => r.data),
        staleTime: 5 * 60 * 1000,
    });
    const availMap = useMemo(() => {
        const m = {};
        (availList || []).forEach((a) => { m[a.game_id] = a; });
        return m;
    }, [availList]);
    // Does this game have a recorded roster/lineup for THIS team's side?
    const hasData = (f) => {
        const side = f.home_team_id === teamId ? "home" : f.away_team_id === teamId ? "away" : null;
        if (!side) return true;
        const a = availMap[f.id];
        if (!a) return true; // availability not loaded / unknown → don't block
        return !!a[`has_${side}_${availType}`];
    };
    const finals = useMemo(
        () => (fixtures || []).filter((f) => (f.status || "").toLowerCase() === "final"),
        [fixtures]);
    const seasons = useMemo(
        () => [...new Set(finals.map((f) => f.season).filter(Boolean))].sort((a, b) => String(b).localeCompare(String(a))),
        [finals]);
    const [season, setSeason] = useState(null);
    const [stage, setStage] = useState(null);
    // Historic view is hidden until the owner switches to it — the page opens on
    // the Active roster/lineup only. Season + stage + game bar stay hidden until then.
    const [histOpen, setHistOpen] = useState(target !== "active" && target != null);
    useEffect(() => { if (target && target !== "active") setHistOpen(true); }, [target]);
    const histLabel = historicLabel || (availType === "lineup" ? "Historic Lineups" : "Historic Rosters");

    // Default to the newest season, and PREFER Regular Season for its stage
    // (never auto-land on Preseason). Falls back to the newest game's own stage.
    useEffect(() => {
        if (season || !finals.length) return;
        const newest = [...finals].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
        const stagesForSeason = new Set(finals.filter((f) => f.season === newest.season).map((f) => f.stage || "regular"));
        setSeason(newest.season);
        setStage(stagesForSeason.has("regular") ? "regular" : (newest.stage || "regular"));
    }, [finals, season]);

    const stages = useMemo(() => {
        const s = [...new Set(finals.filter((f) => f.season === season).map((f) => f.stage || "regular"))];
        return s.sort((a, b) => (STAGE_ORDER[a] ?? 9) - (STAGE_ORDER[b] ?? 9));
    }, [finals, season]);

    const games = useMemo(() => finals
        .filter((f) => f.season === season && (f.stage || "regular") === stage)
        .sort((a, b) => String(a.date).localeCompare(String(b.date))), [finals, season, stage]);

    const onPickSeason = (s) => { setSeason(s); const set = new Set(finals.filter((f) => f.season === s).map((f) => f.stage || "regular")); const st = [...set].sort((a, b) => (STAGE_ORDER[a] ?? 9) - (STAGE_ORDER[b] ?? 9)); setStage(set.has("regular") ? "regular" : (st[0] || "regular")); };

    const GameBtn = ({ f }) => {
        const active = target === f.id;
        const enabled = hasData(f);
        return (
            <button type="button" onClick={() => enabled && onTarget(f.id)} disabled={!enabled} data-testid={`picker-game-${f.id}`}
                title={enabled ? undefined : `No ${availType} recorded for this game`}
                style={active ? { backgroundColor: "rgba(157,76,221,0.22)", borderColor: "rgba(157,76,221,0.5)" } : undefined}
                className={`shrink-0 inline-flex flex-col items-center gap-0.5 rounded-lg border px-2.5 py-1 transition-colors ${active ? "" : enabled ? "border-white/12 bg-black/25 hover:border-white/25" : "border-white/[0.06] bg-black/10 opacity-40 grayscale cursor-not-allowed"}`}>
                <span className="inline-flex items-center gap-1.5">
                    <TeamBadge teamId={f.home_team_id} size="xs" asLink={false} />
                    <span className="font-mono-stat text-[11px] font-bold tabular-nums text-white/85">{f.home_score}–{f.away_score}</span>
                    <TeamBadge teamId={f.away_team_id} size="xs" asLink={false} />
                </span>
                <span className="text-[9px] font-bold uppercase tracking-wider text-white/45">{formatDate(f.date)}{f.overtime ? " · OT" : f.shootout ? " · SO" : ""}{enabled ? "" : " · none"}</span>
            </button>
        );
    };

    return (
        <div className="hidden lg:flex flex-col items-center gap-2 w-full" data-testid="roster-game-picker">
            <div className="flex items-center justify-center gap-2 flex-wrap w-full">
                <NavPageTitle page={titlePage} count={count} testId="picker-league-title" />
                {includeActive && (
                    <button type="button" onClick={() => { onTarget("active"); setHistOpen(false); }} data-testid="picker-active"
                        style={(target === "active" && !histOpen) ? { backgroundColor: "rgba(157,76,221,0.22)", borderColor: "rgba(157,76,221,0.5)", color: "#D9BEF5" } : undefined}
                        className={`inline-flex items-center rounded-lg border px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${(target === "active" && !histOpen) ? "" : "border-white/15 bg-black/25 text-[#C9A6F0] hover:text-white"}`}>
                        {activeLabel}
                    </button>
                )}
                {seasons.length > 0 && (
                    <button type="button" onClick={() => { setHistOpen(true); setOpenGroup("season"); }} data-testid="picker-historic"
                        style={histOpen ? { backgroundColor: "rgba(157,76,221,0.22)", borderColor: "rgba(157,76,221,0.5)", color: "#D9BEF5" } : undefined}
                        className={`inline-flex items-center rounded-lg border px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${histOpen ? "" : "border-white/15 bg-black/25 text-[#C9A6F0] hover:text-white"}`}>
                        {histLabel}
                    </button>
                )}
                {histOpen && seasons.length > 0 && <FilterTrigger id="season" label={season || "SEASON"} testId="picker-season-trigger" />}
                {histOpen && stages.length > 0 && <FilterTrigger id="stage" label={STAGE_LABEL[stage] || stage || "STAGE"} testId="picker-stage-trigger" />}
                {onPosition && <FilterTrigger id="position" label={position && position !== "all" ? position.toUpperCase() : "POS"} testId="picker-position-trigger" />}
                {extra}
                <TeamSortToggle />
            </div>
            {openGroup && (histOpen || openGroup === "position" || openGroup === TEAM_GROUP) && (
                <div className="w-full flex justify-center items-center flex-wrap gap-1.5 border-t border-white/10 pt-2" data-testid="picker-drawer">
                    {openGroup === "season" && histOpen && seasons.length > 0 && (<>
                        <DrawerTitle>Season</DrawerTitle>
                        <SegmentedControl testId="picker-season" value={season || ""} onChange={onPickSeason}
                            options={seasons.map((s) => ({ k: s, l: s, testId: `picker-season-${s}` }))} />
                    </>)}
                    {openGroup === "stage" && histOpen && stages.length > 0 && (<>
                        <DrawerTitle>Stage</DrawerTitle>
                        <SegmentedControl testId="picker-stage" value={stage || ""} onChange={setStage}
                            options={stages.map((s) => ({ k: s, l: STAGE_LABEL[s] || s, testId: `picker-stage-${s}` }))} />
                    </>)}
                    {openGroup === "position" && onPosition && (<>
                        <DrawerTitle>Position</DrawerTitle>
                        <PositionFilterTabs value={position || "all"} onChange={onPosition} testIdPrefix="picker-roster" singleRow order={posOrder || ["all", "fwd", "w", "lw", "c", "rw", "def", "ld", "rd", "g"]} />
                    </>)}
                    {openGroup === TEAM_GROUP && teamSortIds.length > 0 && (<>
                        <DrawerTitle>{allowAll !== false ? "Team Sort" : "Team Pick"}</DrawerTitle>
                        <PwhlTeamButtons teamIds={teamSortIds} selected={sortTeam || ""} onSelect={handleTeamSelect} showAll={allowAll !== false} favTeam={favTeam} align="center" allLabel="ALL" testId="team-bar-buttons" />
                    </>)}
                </div>
            )}
            {histOpen && games.length > 0 && (
                <div className="w-full flex justify-center items-center flex-wrap gap-1.5 border-t border-white/10 pt-2" data-testid="picker-game-bar">
                    {games.map((f) => <GameBtn key={f.id} f={f} />)}
                </div>
            )}
        </div>
    );
}

export default RosterGamePicker;
